import * as React from "react";
import { Link, NavLink, Navigate, Outlet, useLocation, useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Menu, X } from "lucide-react";
import { PRODUCT } from "@mandrac/shared";
import { Button, Select, Spinner, cn } from "@mandrac/ui";
import { homeFor, loginPath } from "../auth/redirect";
import { QueryError } from "../../components/QueryError";
import { api } from "../../lib/api";
import { useApi } from "../../lib/queries";
import { useI18n } from "../../lib/i18n";
import { useMe, type Me } from "../../lib/session";
import { getThemePref, setThemePref, type ThemePref } from "../../lib/theme";
import { ALL_PORTS, allowedPages, pageAt, portSelector, resolvePortChoice, type PortChoice } from "./access";

// ---------------------------------------------------------------------------
// Selected port (shared by admin pages)
//
// One selector in the top bar. Pages that can show every port (overview, invoices: `allPorts` in
// access.ts) also offer "Sve luke"; the other pages always work on one port. Choosing a port
// anywhere selects it everywhere; "Sve luke" is remembered for the pages that support it.

/**
 * Platform admins (support) choose the port authority they work in right in the top bar: picking
 * one opens it as support, "Platforma" ends that. Everyone else sees their authority's name.
 */
function AuthoritySwitcher({ me }: { me: Me }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const list = useApi<Array<{ id: string; name: string }>>(["platform"], me.isPlatformAdmin ? "/platform/authorities" : null);
  const [busy, setBusy] = React.useState(false);
  if (!me.isPlatformAdmin) return <span>{me.authority?.name ?? t("settings.platform")}</span>;
  return (
    <label className="flex items-center gap-2">
      <span className="sr-only">{t("platform.authorities")}</span>
      <Select
        className="h-9 w-auto min-w-48 border-on-bar-muted/50 bg-bar-2 font-semibold text-on-bar max-md:flex-1"
        data-testid="authority-switcher"
        disabled={busy || !list.data}
        value={me.authority?.id ?? ""}
        onChange={async (e) => {
          const id = e.target.value;
          setBusy(true);
          try {
            await api(id ? "/platform/impersonate" : "/platform/stop-impersonating", { body: id ? { authorityId: id } : {} });
            await qc.resetQueries();
            window.location.href = id ? "/admin" : "/admin/settings?tab=platform";
          } finally {
            setBusy(false);
          }
        }}
      >
        <option value="">{t("settings.platform")}</option>
        {(list.data ?? (me.authority ? [{ id: me.authority.id, name: me.authority.name }] : [])).map((a) => (
          <option key={a.id} value={a.id}>
            {a.name}
          </option>
        ))}
      </Select>
    </label>
  );
}

const PORT_KEY = "mandrac.admin.port";
const ALL_PORTS_KEY = "mandrac.admin.allPorts";

interface AdminCtx extends PortChoice {
  me: Me;
  setPortId: (id: string) => void;
}
/** Exported for tests; pages use `useAdmin()`. */
export const AdminContext = React.createContext<AdminCtx | null>(null);
export function useAdmin(): AdminCtx {
  const v = React.useContext(AdminContext);
  if (!v) throw new Error("useAdmin outside AdminLayout");
  return v;
}

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function store(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* ignore */
  }
}

export function AdminLayout() {
  const { t } = useI18n();
  const me = useMe();
  const here = useLocation();
  const [portId, setPortIdState] = React.useState<string | null>(() => readStored(PORT_KEY));
  // Users with several ports start on "Sve luke" (the overview's natural scope) until they pick one.
  const [allPortsPref, setAllPortsPref] = React.useState(() => readStored(ALL_PORTS_KEY) !== "0");

  const data = me.data;
  const choice = resolvePortChoice(data?.ports ?? [], portId, allPortsPref);
  const setPortId = React.useCallback((id: string) => {
    store(PORT_KEY, id);
    store(ALL_PORTS_KEY, "0");
    setPortIdState(id);
    setAllPortsPref(false);
  }, []);
  const selectAllPorts = React.useCallback(() => {
    store(ALL_PORTS_KEY, "1");
    setAllPortsPref(true);
  }, []);

  if (me.isLoading)
    return (
      <div className="p-6">
        <Spinner label={t("common.loading")} />
      </div>
    );
  // A failed /auth/me (network, 5xx) is not a logged-out user: say so instead of sending them to login.
  if (me.isError && !data)
    return (
      <main className="mx-auto max-w-xl p-6">
        <QueryError query={me} />
      </main>
    );
  if (!data) return <Navigate to={loginPath(here.pathname + here.search)} replace />;
  // Dock staff have no office page: send them to the dock instead of a "no access" screen.
  if (homeFor(data) === "/dock") return <Navigate to="/dock" replace />;

  return (
    <AdminContext.Provider value={{ me: data, setPortId, ...choice }}>
      <AdminShell me={data} choice={choice} onPort={(v) => (v === ALL_PORTS ? selectAllPorts() : setPortId(v))} />
    </AdminContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Shell: top bar + toolbar. On narrow screens (< md) both collapse behind a menu button and open
// as a panel under the bar; from md up the layout is the desktop one.

function AdminShell({ me, choice, onPort }: { me: Me; choice: PortChoice; onPort: (v: string) => void }) {
  const { t, locale, setLocale } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [theme, setTheme] = React.useState<ThemePref>(getThemePref);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuButton = React.useRef<HTMLButtonElement>(null);
  const navRef = React.useRef<HTMLElement>(null);
  const mainRef = React.useRef<HTMLElement>(null);

  const ports = me.ports;
  const nav = allowedPages(me);
  const selector = portSelector(pageAt(location.pathname), ports.length, choice);
  const scopeLabel = selector.value === ALL_PORTS ? t("common.allPorts") : ports.find((p) => p.id === choice.portId)?.name;

  // Any navigation (menu link, button on the page, browser back) closes the mobile menu.
  React.useEffect(() => setMenuOpen(false), [location.pathname]);
  // Open: focus moves to the first link in the panel; Escape closes it and returns focus to the button.
  React.useEffect(() => {
    if (!menuOpen) return;
    navRef.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setMenuOpen(false);
      menuButton.current?.focus();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [menuOpen]);

  /** After choosing a page from the mobile menu, close it and move focus to the new page. */
  function onNavigate() {
    if (!menuOpen) return;
    setMenuOpen(false);
    mainRef.current?.focus();
  }

  async function logout() {
    await api("/auth/logout", { method: "POST" }).catch(() => {});
    qc.clear();
    navigate("/login", { replace: true });
  }

  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:bg-surface focus:p-2">
        {t("common.skipToContent")}
      </a>
      <header className="bg-bar text-on-bar">
        <div className="flex min-h-[60px] flex-wrap items-center gap-x-7 gap-y-2 px-4 py-2 sm:px-6 max-md:min-h-14 max-md:gap-x-3">
          <Link
            to={nav[0]?.path ?? "/admin"}
            aria-label={`${PRODUCT.name}: ${t("nav.home")}`}
            onClick={() => setMenuOpen(false)}
            className="pressable flex min-w-0 flex-col text-inherit no-underline"
          >
            <span className="text-15 font-extrabold uppercase tracking-[0.08em]">{PRODUCT.name}</span>
            {scopeLabel ? <span className="truncate text-13 text-on-bar-muted md:hidden">{scopeLabel}</span> : null}
          </Link>
          <button
            ref={menuButton}
            type="button"
            className="ml-auto inline-flex h-10 items-center gap-2 rounded-[8px] border border-on-bar-muted/50 px-3 text-14 font-semibold text-on-bar hover:bg-bar-2 md:hidden"
            aria-expanded={menuOpen}
            aria-controls="admin-nav admin-toolbar"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {menuOpen ? <X aria-hidden="true" size={18} /> : <Menu aria-hidden="true" size={18} />}
            {t("nav.menu")}
          </button>
          <nav ref={navRef} id="admin-nav" aria-label={t("common.mainMenu")} className={cn("flex flex-auto flex-wrap gap-1", "max-md:w-full max-md:flex-col max-md:pb-1", !menuOpen && "max-md:hidden")}>
            {nav.map((n) => (
              <NavLink
                key={n.id}
                to={n.path}
                end={n.segment === ""}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    "pressable rounded-[8px] px-3 py-2 text-14 font-semibold no-underline max-md:py-2.5 max-md:text-15",
                    isActive ? "bg-bar-2 text-on-bar" : "text-on-bar-muted hover:text-on-bar",
                  )
                }
              >
                {t(n.navKey)}
              </NavLink>
            ))}
          </nav>
          <div className={cn("flex flex-wrap items-center gap-3 text-14 text-on-bar-muted", "max-md:w-full max-md:border-t max-md:border-bar-2 max-md:pt-2 max-md:pb-1", !menuOpen && "max-md:hidden")}>
            <AuthoritySwitcher me={me} />
          </div>
        </div>
        {me.impersonating ? (
          <div className="bg-accent px-4 py-1.5 text-14 font-semibold text-white sm:px-6">
            {t("platform.impersonating", { name: me.authority?.name ?? "" })}
          </div>
        ) : null}
      </header>
      <div id="admin-toolbar" className={cn("border-b border-border bg-surface", !menuOpen && "max-md:hidden")}>
        <div className="mx-auto flex max-w-[1440px] flex-wrap items-center gap-3 px-4 py-2 sm:px-6 max-md:py-3">
          {ports.length > 0 ? (
            <label className="flex items-center gap-2 text-13 font-semibold text-muted max-md:w-full">
              {t("common.port")}
              <Select className="h-9 w-auto min-w-40 max-md:flex-1" aria-label={t("common.port")} data-testid="port-switcher" value={selector.value} onChange={(e) => onPort(e.target.value)}>
                {selector.offerAll ? <option value={ALL_PORTS}>{t("common.allPorts")}</option> : null}
                {ports.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          <div className="ml-auto flex flex-wrap items-center gap-2 max-md:ml-0 max-md:w-full max-md:gap-3">
            <label className="flex items-center gap-2 text-13 font-semibold text-muted">
              {t("common.language")}
              <Select className="h-9 w-auto" aria-label={t("common.language")} value={locale} onChange={(e) => setLocale(e.target.value as "hr" | "en")}>
                <option value="hr">HR</option>
                <option value="en">EN</option>
              </Select>
            </label>
            <label className="flex items-center gap-2 text-13 font-semibold text-muted">
              {t("common.theme")}
              <Select
                className="h-9 w-auto"
                aria-label={t("common.theme")}
                value={theme}
                onChange={(e) => {
                  const v = e.target.value as ThemePref;
                  setTheme(v);
                  setThemePref(v);
                }}
              >
                <option value="system">{t("common.themeSystem")}</option>
                <option value="light">{t("common.themeLight")}</option>
                <option value="dark">{t("common.themeDark")}</option>
              </Select>
            </label>
            <div className="flex items-center gap-2 max-md:w-full max-md:justify-between">
              <span className="text-13 text-muted">{me.user.name}</span>
              <Button size="sm" variant="ghost" onClick={logout}>
                {t("common.logout")}
              </Button>
            </div>
          </div>
        </div>
      </div>
      <main id="main" ref={mainRef} tabIndex={-1} className="mx-auto flex max-w-[1440px] flex-col gap-5 px-4 py-6 outline-none sm:px-6">
        <Outlet />
      </main>
    </>
  );
}
